<template>
  <div>
    <div class="row">
      <div class="col">
        <div
          v-if="showTargetFilter"
          class="row items-center q-col-gutter-xs"
        >
          <filter-chips-bar
            :filters="filters"
            :filter-criteria="targetFilterCriteria"
            @update:filters="onFiltersChanged"
          />
        </div>
        <div
          v-if="!noBulkActions"
          class="q-my-sm q-py-sm full-width rounded-borders flex targets-actions-wrapper"
          :class="{
            'bg-primary-light': selectedTargetsCount > 0,
            'q-pl-md': $q.screen.gt.sm
          }"
        >
          <div
            class="row col"
            :class="$q.screen.gt.sm ? 'items-center' : 'items-start'"
          >
            <q-checkbox
              v-if="activeProgramTargetsCount > 0"
              :model-value="allTargetsSelected"
              size="xs"
              class="q-mr-sm col-auto text-overline text-dark-variant text-uppercase"
              :class="{ 'q-ml-lg': $q.screen.gt.sm }"
              @update:model-value="onSelectAllTargets"
            >
              <p
                v-if="selectedTargetsCount === 0"
                class="q-ma-none"
              >
                {{ $t('editProgram.textSelectAllTargets') }}
              </p>
              <p
                v-else
                class="q-ma-none"
              >
                {{
                  $tc('editProgram.textSelectedTargets', selectedTargetsCount, {
                    count: selectedTargetsCount
                  })
                }}
              </p>
            </q-checkbox>
          </div>
          <div
            v-if="selectedTargetsCount === 0"
            class="row col-auto items-center justify-end q-col-gutter-sm"
          >
            <template
              v-if="activeProgramTargetsCount > 0"
            >
              <div>
                <q-btn
                  v-if="showDiscriminativeStimulusToggle"
                  :label="$t('editProgram.buttonAddSD')"
                  size="sm"
                  icon="feedback"
                  text-color="white"
                  class="col-auto"
                  :padding="$q.screen.gt.sm ? '4px 16px' : '4px 10px'"
                  :class="{
                    'bg-primary-variant' : !showDiscriminativeStimulus,
                    'bg-positive' : showDiscriminativeStimulus
                  }"
                  @click="showDiscriminativeStimulus = !showDiscriminativeStimulus"
                >
                  <q-tooltip>
                    {{ $t('editProgram.toggleSetDiscriminativeStimulus') }}
                  </q-tooltip>
                </q-btn>
              </div>
              <div>
                <q-btn-dropdown
                  :label="$q.screen.gt.sm ? $t('editProgram.buttonSortTargets') : null"
                  icon="sort"
                  size="sm"
                  color="primary-variant"
                  :padding="$q.screen.gt.sm ? '4px 16px' : '4px 10px'"
                >
                  <q-list>
                    <q-item
                      v-close-popup
                      clickable
                      @click="sortTargetsByLabel('asc')"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('editProgram.sortTargetsLabelAsc') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="sortTargetsByLabel('desc')"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('editProgram.sortTargetsLabelDesc') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="sortTargetsByIntroDate('asc')"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('editProgram.sortTargetsIntroDateAsc') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="sortTargetsByIntroDate('desc')"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('editProgram.sortTargetsIntroDateDesc') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="sortTargetsByStatus('asc')"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('editProgram.sortTargetsByStatusAsc') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="sortTargetsByStatus('desc')"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('editProgram.sortTargetsByStatusDesc') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </div>
            </template>
            <div
              v-if="!disableAddTarget"
            >
              <q-btn
                :label="$q.screen.gt.sm ? $t('editProgram.buttonSuggestTargets') : null"
                icon="chat"
                size="sm"
                color="primary-variant"
                class="col-auto"
                :padding="$q.screen.gt.sm ? '4px 16px' : '4px 10px'"
                data-e2e="button-suggest-targets"
                @click="onSuggestTargets"
              />
            </div>
            <div
              v-if="!disableAddTarget"
            >
              <q-btn
                :label="$q.screen.gt.sm ? $t('editProgram.buttonImportTargets') : null"
                icon="system_update_alt"
                size="sm"
                color="primary-variant"
                class="col-auto"
                :padding="$q.screen.gt.sm ? '4px 16px' : '4px 10px'"
                @click="onImportTargets"
              />
            </div>
            <div>
              <q-btn
                flat
                round
                dense
                color="dark-variant"
                icon="more_vert"
              >
                <q-menu>
                  <q-list style="min-width: 100px">
                    <q-item
                      v-ripple
                      v-close-popup
                      :disable="hasFilters"
                      clickable
                    >
                      <q-toggle
                        v-model="showTargetFilter"
                        left-label
                        :disable="hasFilters"
                        :label="$t('programs.targetList.showTargetFilter')"
                      />
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="onExportTargetsToClipboard"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ $t('programs.targetList.exportToClipboard') }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-expansion-item
                      v-if="!hasParentTarget && canSetTargetDataType"
                      :label="$t('programs.targetList.settingDefaultTargetType')"
                      expand-separator
                    >
                      <q-item
                        v-for="targetDataType in availableTargetDataTypes"
                        :key="targetDataType.value"
                        v-close-popup
                        clickable
                        :active="targetDataType.value === defaultTargetType"
                        :inset-level="0.5"
                        @click="onSetDefaultTargetType(targetDataType.value)"
                      >
                        <q-item-section>
                          <q-item-label>
                            {{ targetDataType.label }}
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-expansion-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </div>
          <div
            v-if="selectedTargetsCount > 0"
            class="col-auto q-col-gutter-sm q-pr-md"
            :class="$q.screen.gt.sm ? 'items-center row' : 'items-baseline column q-px-sm'"
          >
            <div>
              <q-btn-dropdown
                v-if="!disableDiscriminativeStimulus"
                :label="$t('editProgram.buttonSetDiscriminativeStimulus')"
                color="secondary"
                size="sm"
                :menu-self="$q.screen.gt.sm ? 'top end' : 'top middle'"
                :menu-offset="[0, 3]"
              >
                <q-card>
                  <q-card-section>
                    <p>
                      {{ $t('editProgram.setDiscriminativeStimulusLabel') }}
                    </p>
                    <q-select
                      v-model="discriminateStimulusValue"
                      :label="$t('programs.targetCard.inputDiscriminativeStimulusLabel')"
                      use-input
                      hide-selected
                      fill-input
                      input-debounce="0"
                      :options="filteredDiscriminativeStimulusList"
                      dense
                      outlined
                      @filter="onFilterDiscriminativeStimulusList"
                      @input-value="onSetDiscriminativeStimulusListValue"
                      @keydown.enter.prevent="onBulkSetDiscriminativeStimulus"
                    />
                  </q-card-section>
                  <q-card-actions align="right">
                    <q-btn
                      v-close-popup
                      flat
                      :label="$t('common.buttonCancel')"
                      color="secondary"
                      @click="clearDiscriminativeStimulus"
                    />
                    <q-btn
                      v-close-popup
                      flat
                      :label="$t('common.buttonSave')"
                      type="submit"
                      color="primary"
                      @click="onBulkSetDiscriminativeStimulus"
                    />
                  </q-card-actions>
                </q-card>
              </q-btn-dropdown>
            </div>
            <div>
              <q-btn-dropdown
                :label="$t('editProgram.buttonSetStatus')"
                color="secondary"
                size="sm"
                @before-show="getAvailableWorkflowStatusesForAllTargets"
                @hide="isSetStatusOpened = false"
              >
                <q-list>
                  <q-item
                    v-for="targetStatus in availableWorkflowStatusesForAllTargets"
                    :key="targetStatus.value"
                    v-close-popup
                    clickable
                    @click="onBulkSetStatus(targetStatus.value)"
                  >
                    <q-item-section avatar>
                      <q-icon
                        :name="targetStatus.icon"
                        :color="targetStatus.color"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ targetStatus.label }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </div>
            <div>
              <q-btn
                :label="$t('editProgram.buttonDeleteTarget')"
                icon="delete"
                size="sm"
                color="secondary"
                @click="onDeleteProgramMultipleTarget"
              />
            </div>
          </div>
        </div>

        <!-- Banner for any invalid target status -->
        <banner
          v-if="showInvalidStatusBanner"
          type="warning"
          show-icon
          :message="$t('programs.targetCard.bannerInvalidStatus')"
          class="q-mb-md"
        />

        <draggable
          v-bind="dragOptions"
          v-model="paginatedTargets"
          class="list-group q-mb-md row q-col-gutter-md"
          tag="div"
          item-key="id"
          handle=".target-drag-handle"
          :component-data="!drag ? { name: 'flip-list' } : null"
          :force-fallback="true"
          :disabled="hasFilters"
          @start="drag = true"
          @end="drag = false"
        >
          <template #item="{ element: target, index }">
            <div
              v-show="showTargetCards(index)"
              class="col-12 row"
            >
              <target-card
                :ref="target.id"
                v-model="programTargets"
                :program="program"
                :target-id="target.id"

                :selected="selectedTargets.includes(target.id)"
                :discriminative-stimulus-list="discriminativeStimulusList"
                :force-show-discriminative-stimulus="showDiscriminativeStimulus || targetsHasDiscriminativeStimulus"
                :target-duplicate-warning="isDuplicateTarget(target)"
                :selectable="!noBulkActions"
                :no-bulk-actions="noBulkActions"
                :editable="!readOnly"
                :disable="disable"
                :enter-adds-target="enterAddsTarget(target)"
                :program-workflow="programWorkflow"
                :package-prompt-level-templates="packagePromptLevelTemplates"
                :should-load-sub-targets="shouldLoadSubTargets"

                @enter="onTargetEnterKey(target)"
                @select="onSelectTarget($event, target.id)"
                @delete="onDeleteTarget"
                @duplicate="onDuplicateTarget"
                @load-subtargets="$emit('load-subtargets', $event)"
                @subtargets-loaded="$emit('subtargets-loaded', $event)"
                @change-target-type="$emit('change-target-type', $event)"
                @update-target-progress-settings="$emit('update-target-progress-settings', $event)"
                @update-parent-target-status="$emit('update-parent-target-status', $event)"
              />
            <!-- @todo: these last 3 emits passing mean that we probably need to move some targets logic to vuex -->
            </div>
          </template>
        </draggable>

        <div
          v-if="maxPaginationPages > 1"
          class="row q-mb-md justify-end"
        >
          <q-pagination
            v-if="maxPaginationPages > 1"
            v-model="pagination.currentPage"
            :max="maxPaginationPages"
            :max-pages="$q.screen.lt.sm ? 4 : 6"
            :min="1"
            :boundary-numbers="false"
            dense
            boundary-links
            icon-first="skip_previous"
            icon-last="skip_next"
            direction-links
            icon-prev="fast_rewind"
            icon-next="fast_forward"
          />

          <q-select
            v-if="maxPaginationPages > 1"
            v-model="pagination.targetsPerPage"
            :options="recordsPerPageOptions"
            emit-value
            map-options
            outlined
            :label="$t('editProgram.recordsPerPage')"
            class="col-xs-5 col-sm-4 col-md-2"
            dense
          />
        </div>

        <div
          v-if="!disableAddTarget"
          class="col-12 row"
        >
          <q-btn-dropdown
            v-if="canSetTargetDataType"
            color="primary"
            size="md"
            split
            :label="addTargetLabel"
            class="full-width"
            data-e2e="button-add-target"
            @click="onAddTargetLabel(null)"
          >
            <q-list>
              <q-item
                v-for="targetDataType in availableTargetDataTypes"
                :key="targetDataType.value"
                v-close-popup
                clickable
                :inset-level="0.5"
                @click="onAddTargetLabel(targetDataType.value)"
              >
                <q-item-section>
                  <q-item-label>
                    {{ targetDataType.label }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
          <q-btn
            v-else
            outline
            color="primary"
            size="md"
            :label="addTargetLabel"
            class="full-width bg-white"
            data-e2e="button-add-target"
            @click="onAddTargetLabel(null)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { Clipboard } from '@capacitor/clipboard';
import { filterPropertyTypes, targetDataTypes, targetStatuses } from 'boot/globals';
import ImportTargets from 'components/Programs/Modals/ImportTargets';
import SuggestTargets from 'components/Programs/Modals/SuggestTargets';
import TargetCard from 'components/Programs/TargetCard';
import Banner from 'components/UI/Banner';
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import { Timestamp } from 'firebase/firestore';
import {
  chain, concat,
  filter, find, findIndex, flatten, get, includes, isArray, isEmpty,
  isFunction, map, orderBy, reject, some, sortBy, toLower,
} from 'lodash';
import { intersectionBy } from 'lodash/array';
import { mapState } from 'pinia';
import { copyToClipboard, uid } from 'quasar';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { workflows } from 'src/store/workflows';
import draggable from 'vuedraggable';

export default {
  name: 'TargetList',

  components: {
    TargetCard,
    draggable,
    Banner,
    FilterChipsBar,
  },

  mixins: [
    Targets,
    Toolbox,
    Interactions,
    FiltersMixin,
  ],

  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    parentTarget: {
      type: String,
      default: null,
    },
    program: {
      type: Object,
      required: true,
    },
    disableDiscriminativeStimulus: {
      type: Boolean,
      default: false,
    },
    noBulkActions: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    disableAddTarget: {
      type: Boolean,
      default: false,
    },
    programWorkflow: {
      type: String,
      default: null,
    },
    packagePromptLevelTemplates: {
      type: Array,
      default: () => [],
    },
    shouldLoadSubTargets: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:modelValue',
    'load-subtargets',
    'update-program-property',
    'subtargets-loaded',
    'change-target-type',
    'update-target-progress-settings',
    'update-parent-target-status',
  ],

  data() {
    return {
      availableWorkflowStatusesForAllTargets: [],
      isSetStatusOpened: false,
      showDiscriminativeStimulus: false,
      showTargetFilter: false,
      filters: {},

      drag: false,
      dragOptions: {
        animation: 200,
        group: this.parentTarget || 'ROOT',
        disabled: false,
        ghostClass: 'ghost',
        direction: 'horizontal',
      },

      discriminateStimulusValue: '',
      filteredDiscriminativeStimulusList: [],

      pagination: {
        currentPage: 1,
        targetsPerPage: 50,
      },
      recordsPerPageOptions: [
        ...map([25, 50, 100], (value) => ({ label: value, value })),
        { label: this.$t('editProgram.recordsAll'), value: 'all' },
      ],

      selectedTargets: [],
    };
  },

  computed: {
    targetFilterCriteria() {
      return [
        {
          value: 'status',
          filterProperty: 'status',
          label: this.$t('programs.targets.properties.status'),
          filterType: filterPropertyTypes.StringInArray,
          options: {
            filterCriteriaItems: this.processedTargetStatuses,
          },
        },
      ];
    }, // targetFilterCriteria()

    ...mapState(workflows, ['workflowStatuses']),

    availableTargetDataTypes() {
      return this.getAvailableTargetDataTypes(
        get(this.program, 'type'),
        this.parentTarget !== null,
      );
    }, // availableTargetDataTypes()

    activeProgramTargets: {
      get() {
        if (this.parentTarget === null) {
          return chain(this.modelValue)
            .reject((target) => target.editStatus === 'delete' || Boolean(target.parentTarget))
            .orderBy('order')
            .value();
        }
        return chain(this.modelValue)
          .filter((target) => target.editStatus !== 'delete' && target.parentTarget === this.parentTarget)
          .orderBy('order')
          .value();
      },

      set(reorderedProgramTargets) {
        this.programTargets = concat(
          reject(this.programTargets, (target) => includes(this.activeProgramTargets, target)),
          map(reorderedProgramTargets, (programTarget, index) => ({
            ...programTarget,
            order: (index + 1) + (this.targetsPerPage * (this.pagination.currentPage - 1)),
            editStatus: programTarget.editStatus === 'create' ? 'create' : 'update',
          })),
        );
      },
    }, // activeProgramTargets()

    filteredActiveProgramTargets() {
      return this.getFilteredElements(this.activeProgramTargets, this.filters);
    }, // filteredActiveProgramTargets()

    showTargetCards() {
      return (cardIndex) => {
        if (!this.isSetStatusOpened) {
          return true;
        }

        return cardIndex >= (this.targetsPerPage * (this.pagination.currentPage - 1));
      };
    }, // showTargetCards()

    paginatedTargets: {
      get() {
        return this.pagination.targetsPerPage === 'all' || this.isSetStatusOpened
          ? this.filteredActiveProgramTargets
          : chain(this.filteredActiveProgramTargets)
            .drop(this.targetsPerPage * (this.pagination.currentPage - 1))
            .take(this.targetsPerPage)
            .value();
      },

      set(reorderedProgramTargets) {
        this.programTargets = concat(
          reject(this.programTargets, (target) => includes(this.filteredActiveProgramTargets, target)),
          map(this.filteredActiveProgramTargets, (programTarget) => {
            const reorderedTargetIdx = findIndex(reorderedProgramTargets, { id: programTarget.id });

            if (reorderedTargetIdx !== -1) {
              return {
                ...programTarget,
                order: (reorderedTargetIdx + 1) + (this.targetsPerPage * (this.pagination.currentPage - 1)),
                editStatus: programTarget.editStatus === 'create' ? 'create' : 'update',
              };
            }

            return programTarget;
          }),
        );
      },
    }, // paginatedTargets()

    programTargets: {
      get() {
        return this.modelValue;
      },
      set(targets) {
        this.$emit('update:modelValue', targets);
      },
    }, // programTargets()

    parentTargetType() {
      return chain(this.modelValue)
        .find({ id: this.parentTarget })
        .get('dataType', null)
        .value();
    }, // parentTargetType()

    addTargetLabel() {
      if (!this.parentTarget) {
        if ((this.defaultTargetType || 'discreteTrial') === 'discreteTrial') {
          return this.$t('editProgram.buttonAddTarget');
        }

        const targetDataTypeLabel = targetDataTypes
          .find((targetDataType) => (targetDataType.value === this.defaultTargetType))?.label;

        if (!targetDataTypeLabel) {
          return this.$t('editProgram.buttonAddTarget');
        }
        return this.$t('editProgram.buttonAddTargetType', { targetDataTypeLabel });
      }

      const parentTarget = find(this.modelValue, { id: this.parentTarget });

      const parentTargetDataType = get(parentTarget, 'dataType', null);
      const parentTargetLabel = get(parentTarget, 'label', null);

      if (parentTargetDataType === 'taskAnalysis') {
        return this.$t('editProgram.buttonAddSubTargetTA', { parentTargetLabel });
      }

      return this.$t('editProgram.buttonAddSubTarget', { parentTargetLabel });
    }, // parentTargetLabel()

    hasParentTarget() {
      return this.parentTarget !== null;
    },

    targetsHasDiscriminativeStimulus() {
      return some(this.activeProgramTargets, 'discriminativeStimulus');
    }, // targetsHasDiscriminativeStimulus()

    showDiscriminativeStimulusToggle() {
      return !this.disableDiscriminativeStimulus && !this.targetsHasDiscriminativeStimulus;
    }, // showDiscriminativeStimulusToggle()

    discriminativeStimulusList() {
      return chain(this.activeProgramTargets)
        .map((target) => (target.discriminativeStimulus || ''))
        .compact()
        .uniqBy(toLower)
        .value();
    }, // discriminativeStimulusList()

    discriminativeStimulusListTrigger() {
      return this.discriminativeStimulusList.toString();
    }, // discriminativeStimulusListTrigger()

    canSetTargetDataType() {
      return (['simple', 'sbt', 'asr', 'ptp'].includes(get(this.program, 'type')))
        && (this.parentTarget === null);
    }, // canSetTargetDataType()

    activeProgramTargetsCount() {
      return this.activeProgramTargets.length;
    },

    allTargetsSelected() {
      return (this.activeProgramTargetsCount === this.selectedTargetsCount);
    }, // allTargetsSelected()

    targetStatuses() {
      return targetStatuses;
    }, // targetStatuses()

    maxPaginationPages() {
      return Math.ceil(this.activeProgramTargetsCount / this.targetsPerPage);
    }, // maxPaginationPages()

    selectedTargetsCount() {
      return this.selectedTargets.length;
    }, // selectedTargetsCount()

    targetsPerPage() {
      return this.pagination.targetsPerPage === 'all'
        ? this.activeProgramTargetsCount
        : this.pagination.targetsPerPage;
    },

    defaultTargetType() {
      if (this.parentTarget === null) {
        const defaultTargetType = this.program?.defaultTargetType || null;
        if (this.availableTargetDataTypes.find((targetDataType) => (targetDataType.value === defaultTargetType))) {
          return defaultTargetType;
        }
        return null;
      }
      return null;
    },

    hasFilters() {
      return !isEmpty(this.filters);
    }, // hasFilters()

    processedTargetStatuses() {
      const noTotals = isEmpty(this.activeProgramTargets);
      return map(
        targetStatuses,
        (targetStatus) => (
          {
            ...targetStatus,
            label: targetStatus.label
            + (noTotals
              ? ''
              : ` (${
                chain(this.activeProgramTargets)
                  .filter({ status: targetStatus.value })
                  .size()
                  .value()
              })`),
            value: targetStatus.type.toLowerCase(),
          }
        ),
      );
    }, // processedTargetStatuses()

    programSrTarget() {
      return get(this.program, 'srTarget', null);
    }, // programSrTarget()

    availableTargetWorkflowStatuses() {
      return this.$refs[this.paginatedTargets[0]?.id]?.availableTargetWorkflowStatuses;
    }, // availableTargetWorkflowStatuses()

    showInvalidStatusBanner() {
      if (!this.paginatedTargets.length || !this.programWorkflow) {
        return false;
      }

      const workflowStatuses = this.workflowStatuses[this.programWorkflow];
      if (!workflowStatuses) {
        return false;
      }

      return this.paginatedTargets.some((target) => {
        return !Object.keys(workflowStatuses).includes(target.status);
      });
    }, // showInvalidStatusBanner()

  }, // computed

  watch: {
    discriminativeStimulusListTrigger() {
      this.filteredDiscriminativeStimulusList = this.discriminativeStimulusList;
    },

    'pagination.targetsPerPage': {
      handler() {
        this.pagination.currentPage = 1;
      },
    },
  },

  methods: {
    getAvailableWorkflowStatusesForAllTargets() {
      this.isSetStatusOpened = true;
      this.$nextTick(() => {
        const selectedWorkflowStatuses = intersectionBy(
          ...map(this.selectedTargets, (id) => (get(this.$refs, [id, 'availableTargetWorkflowStatuses']))),
          'value',
        );

        this.availableWorkflowStatusesForAllTargets = sortBy(selectedWorkflowStatuses, 'order');
      });
    }, // availableWorkflowStatusesForAllTargets()

    onFiltersChanged(filters) {
      this.pagination.currentPage = 1;
      this.filters = filters;
    }, // onFiltersChanged()

    onAddTargetLabel(dataType = null) {
      this.addTarget(dataType || this.defaultTargetType);
    }, // onAddTargetLabel()

    onSuggestTargets() {
      this.openComponentDialog(
        SuggestTargets,
        null,
        (targets) => {
          this.importTargets(targets);
        },
      );
    },

    onImportTargets() {
      this.openComponentDialog(
        ImportTargets,
        null,
        (targets) => {
          this.importTargets(targets);
        },
      );
    }, // onImportTargets()

    async onExportTargetsToClipboard() {
      const exportData = chain(this.activeProgramTargets)
        .map('label')
        .compact()
        .join('\n')
        .value();
      if (this.$q.platform.is.capacitor) {
        Clipboard.write({
          string: exportData,
        });
      } else {
        await copyToClipboard(exportData);
      }

      this.notifySuccess(this.$t('programs.targetList.targetsExported'));
    },

    // Target methods
    onTargetEnterKey(target) {
      if (this.enterAddsTarget(target)) {
        this.addTarget(this.defaultTargetType || null);
      }
    }, // onTargetEnterKey()

    onDuplicateTarget(sourceTarget) {
      this.$emit('load-subtargets', { targetId: sourceTarget.id, callback: () => this.duplicateTarget(sourceTarget) });
    }, // onDuplicateTarget()

    onDeleteTarget({ id }) {
      this.$emit('load-subtargets', { targetId: id, callback: () => this.deleteTarget(id) });
    }, // onDeleteTarget()

    deleteTarget(deleteTargetId) {
      const targetDescendants = this.getTargetDescendants(deleteTargetId);
      // Mark targets (and sub-targets) for deletion or delete them immediately if they were not saved yet
      const newValue = chain(this.modelValue)
        .reject((target) => (
          (target.editStatus === 'create')
            && ((target.id === deleteTargetId) || (target.parentTarget === deleteTargetId))
        ))
        .map((target) => (
          ((target.id === deleteTargetId) || targetDescendants.includes(target.id))
            ? { ...target, editStatus: 'delete' } : target
        ))
        .value();

      const deleteTargetHasLabel = chain(this.modelValue).find({ id: deleteTargetId }).get('label').value();
      const promptForDelete = !deleteTargetHasLabel || find(newValue, { id: deleteTargetId, editStatus: 'delete' });

      if (!promptForDelete) {
        this.$emit('update:modelValue', newValue);
        return;
      }

      const dialogMessage = (this.programSrTarget && (this.programSrTarget === deleteTargetId))
        ? `${this.$t('editProgram.dialogDeleteSrTargetMessage')} ${this.$t('editProgram.dialogDeleteTargetMessage')}`
        : this.$t('editProgram.dialogDeleteTargetMessage');

      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        dialogMessage,
        () => {
          this.$emit('update:modelValue', newValue);

          if (this.selectedTargets.includes(deleteTargetId)) {
            // deselect deleted target
            this.onSelectTarget(null, deleteTargetId);
          }
        },
      );
    }, // deleteTarget()

    /**
     * Recursively gets all descendant target IDs for a given target id
     *
     * @param {string} currentTargetId
     */
    getTargetDescendants(currentTargetId) {
      const targetDescendants = [];

      chain(this.modelValue)
        .filter({ parentTarget: currentTargetId })
        .forEach(({ id }) => {
          targetDescendants.push(id);
          const targetHasSubtargets = chain(this.modelValue).filter({ parentTarget: id }).size().value() > 0;

          if (targetHasSubtargets) {
            targetDescendants.push(...this.getTargetDescendants(id));
          }
        })
        .value();

      return targetDescendants;
    }, // getTargetDescendants()

    onSelectTarget(value, targetId) {
      if (value) {
        this.selectedTargets.push(targetId);
      } else {
        this.selectedTargets.splice(this.selectedTargets.indexOf(targetId), 1);
      }
    }, // onSelectTarget()

    onSelectAllTargets(selected) {
      this.selectedTargets = selected ? [...map(this.activeProgramTargets, 'id')] : [];
    }, // onSelectAllTargets()

    onSetDefaultTargetType(defaultTargetType) {
      this.$emit('update-program-property', 'defaultTargetType', defaultTargetType);
    },

    getTargetProperty(targetStatus, property, defaultValue = '') {
      return get(get(targetStatuses, targetStatus, {}), property, defaultValue);
    },

    onBulkSetStatus(status) {
      this.selectedTargets.forEach((targetId) => {
        const targetIdx = findIndex(this.programTargets, { id: targetId });

        const target = this.programTargets[targetIdx];

        let targetChanges = { status };

        if (this.getTargetProperty(status, 'resetMasteryDate', false)) {
          targetChanges = { ...targetChanges, masteryDate: null };
        }

        if (this.getTargetProperty(status, 'resetIntroductionDate', false)) {
          targetChanges = { ...targetChanges, introductionDate: null };
        }

        if (this.getTargetProperty(status, 'resetHoldDate', false)) {
          targetChanges = { ...targetChanges, holdDate: null };
        }

        if (['mastered', 'closed'].includes(status) && !target.masteryDate) {
          targetChanges = {
            ...targetChanges,
            masteryDate: Timestamp.now(),
          };
        }

        if (['hold'].includes(status) && !target.holdDate) {
          targetChanges = { ...targetChanges, holdDate: Timestamp.now() };
        }

        const updatedTarget = {
          ...this.programTargets[targetIdx],
          ...targetChanges,
          editStatus: this.programTargets[targetIdx].editStatus === 'create' ? 'create' : 'update',
        };

        this.programTargets.splice(targetIdx, 1, updatedTarget);
      });
    }, // onBulkSetStatus()

    onDeleteProgramMultipleTarget() {
      const dialogMessage = this.selectedTargets.includes(this.programSrTarget)
        ? `${this.$t('editProgram.srTargetInSelectedTargets')} ${this.$t('editProgram.dialogDeleteTargetMessage')}`
        : this.$t('editProgram.dialogDeleteMultipleTargetMessage', { n: this.selectedTargetsCount });

      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        dialogMessage,
        async () => {
          const targetsWithDescendants = await Promise.all(
            this.selectedTargets.map((targetId) => new Promise((resolve) => {
              const targetType = this.modelValue.find(({ id }) => id === targetId).dataType;
              const targetHasSubTargets = targetDataTypes.find(({ value }) => value === targetType)?.hasSubTargets;

              if (!targetHasSubTargets) {
                resolve([targetId]);
                return;
              }

              this.$emit('load-subtargets', {
                targetId,
                callback: () => resolve([targetId, ...this.getTargetDescendants(targetId)]),
              });
            })),
          );

          // Combine all targets and sub-targets into one array
          const targetIdsToDelete = new Set(flatten(targetsWithDescendants));

          // Mark targets (and sub-targets) for deletion or delete them immediately if they were not saved yet
          const newValue = this.modelValue
            .filter((target) => !(target.editStatus === 'create' && targetIdsToDelete.has(target.id)))
            .map((target) => (
              targetIdsToDelete.has(target.id)
                ? { ...target, editStatus: 'delete' }
                : target
            ));

          this.$emit('update:modelValue', newValue);
          this.selectedTargets = [];
        },
      );
    },

    isDuplicateTarget(target) {
      return chain(this.activeProgramTargets)
        .countBy({ label: target.label })
        .get(true)
        .value() > 1;
    }, // isDuplicateTarget()

    clearDiscriminativeStimulus() {
      this.discriminateStimulusValue = '';
    }, // clearDiscriminativeStimulus

    onBulkSetDiscriminativeStimulus() {
      this.selectedTargets.forEach((targetId) => {
        const targetIdx = findIndex(this.programTargets, { id: targetId });

        const updatedTarget = {
          ...this.programTargets[targetIdx],
          discriminativeStimulus: this.discriminateStimulusValue,
          editStatus: this.programTargets[targetIdx].editStatus === 'create' ? 'create' : 'update',
        };

        this.programTargets.splice(targetIdx, 1, updatedTarget);
      });

      this.onSelectAllTargets(false);
      this.clearDiscriminativeStimulus();
    }, // onBulkSetDiscriminativeStimulus

    onFilterDiscriminativeStimulusList(val, update) {
      update(() => {
        const needle = val.toLocaleLowerCase();
        this.filteredDiscriminativeStimulusList = filter(
          this.discriminativeStimulusList,
          (v) => v.toLocaleLowerCase().indexOf(needle) > -1,
        );
      });
    }, // onFilterDiscriminativeStimulusList()

    onSetDiscriminativeStimulusListValue(val) {
      this.discriminateStimulusValue = val;
    }, // onSetDiscriminativeStimulusListValue()

    sortTargetsByLabel(order) {
      this.activeProgramTargets = this.naturalSort(this.activeProgramTargets, 'label', order);
    }, // sortTargetsByLabel()

    sortTargetsByIntroDate(order) {
      this.activeProgramTargets = orderBy(
        this.activeProgramTargets,
        ['introductionDate', 'label'],
        [order, 'asc'],
      );
    }, // sortTargetsByIntroDate()

    sortTargetsByStatus(order) {
      this.activeProgramTargets = orderBy(
        this.activeProgramTargets,
        [(target) => {
          return findIndex(this.availableTargetWorkflowStatuses, (targetStatus) => {
            const isEqualByType = (targetStatus.type === target.status);
            const isEqualByValue = (targetStatus.value?.toLowerCase() === target.status?.toLowerCase());
            return isEqualByType || isEqualByValue;
          });
        }, 'label'],
        [order, 'asc'],
      );
    }, // sortTargetsByStatus()

    addMultipleTargets(labels) {
      const prevOrder = chain(this.activeProgramTargets)
        .maxBy('order')
        .get('order')
        .defaultTo(0)
        .value();

      const newTargets = map(labels, (label, labelIndex) => ({
        id: uid(),
        type: 'label',
        label,
        status: 'waiting',
        parentTarget: this.parentTarget,
        editStatus: 'create',
        order: prevOrder + labelIndex + 1,
      }));

      this.$emit('update:modelValue', concat(this.modelValue, newTargets));
    }, // addMultipleTargets()

    // Program Target
    /**
     * Adds a new target to program's list of targets
     */
    addTarget(dataType = null) {
      const prevOrder = chain(this.activeProgramTargets)
        .maxBy('order')
        .get('order')
        .defaultTo(0)
        .value();

      const newTarget = {
        id: uid(),
        type: 'label',
        label: '',
        status: 'waiting',
        parentTarget: this.parentTarget,
        ...((dataType !== null) ? { dataType } : {}),
        editStatus: 'create',
        order: prevOrder + 1,
      };

      this.$emit('update:modelValue', concat(this.modelValue, newTarget));

      this.$nextTick(() => {
        if (this.maxPaginationPages > 1 && this.maxPaginationPages !== this.pagination.currentPage) {
          this.pagination.currentPage = this.maxPaginationPages;
        }
      });
    }, // addTarget()

    enterAddsTarget(target) {
      return !this.disableAddTarget && chain(this.activeProgramTargets).last().isEqual(target).value();
    }, // enterAddsTarget()

    importTargets(targets) {
      if (!isArray(targets) || targets.length === 0) {
        return;
      }
      this.$q.loading.show();
      this.addMultipleTargets(targets);
      this.notifySuccess(this.$t('editProgram.notifyTargetImportSuccess', {
        numTargets: targets.length,
      }));

      this.$q.loading.hide();
    }, // importTargets()

    onChangeTargetType({ targetId, targetDataType }) {
      const targetCardOnChangeTargetType = chain(this.$refs)
        .get(targetId)
        .get('onChangeTargetType')
        .value();
      if (isFunction(targetCardOnChangeTargetType)) {
        targetCardOnChangeTargetType(targetDataType);
      }
    }, // onChangeTargetType()

    duplicateTarget(sourceTarget) {
      const prevOrder = chain(this.activeProgramTargets)
        .maxBy('order')
        .get('order')
        .defaultTo(0)
        .value();

      const defaultParams = {
        status: 'waiting',
        maintenanceInterval: null,
        introductionDate: null,
        masteryDate: null,
        abortedDate: null,
        holdDate: null,
        lastHit: null,
        progressSettings: null,
      };

      const duplicateTargetRecursive = (sourceTargetObj, parentTargetId, labelSuffix = '', initialOrder = 1) => {
        const newProps = {
          id: uid(),
          editStatus: 'create',
          parentTarget: parentTargetId,
          label: `${sourceTargetObj.label}${labelSuffix}`,
          ...defaultParams,
        };
        if (initialOrder !== null) {
          newProps.order = initialOrder;
        }
        const newTarget = chain(sourceTargetObj)
          .cloneDeep()
          .assign(newProps)
          .omit(['createTimestamp', 'updateTimestamp'])
          .value();
        const subTargets = chain(this.modelValue)
          .filter({ parentTarget: sourceTargetObj.id })
          .reject({ editStatus: 'delete' })
          .flatMap((target) => duplicateTargetRecursive(target, newTarget.id, '', target.order))
          .value();
        return [newTarget, ...subTargets];
      };

      const newValue = concat(
        this.modelValue,
        duplicateTargetRecursive(sourceTarget, sourceTarget.parentTarget, ' (copy)', prevOrder + 1),
      );

      this.$emit('update:modelValue', newValue);

      this.notifySuccess(this.$t(
        'editProgram.notifyTargetDuplicateSuccess',
        { label: `${sourceTarget.label} (copy)` },
      ));
    }, // duplicateTarget()
  },
};
</script>
<style lang="scss">
.ghost {
  opacity:0.1;
}

.targets-actions-wrapper {
  flex-direction: row;
  flex-wrap: wrap;
  > .col {
    min-width: max-content;
  }
}

@media only screen and (max-width: 380px) {
  .targets-actions-wrapper {
    flex-direction: column;
  }
}
</style>
